<template>
  <div>
    <container-wrapper v-slot="slotProps" :element-key="elementKey" :element-data="elementData"
      display-name="People also ask" icon-name="question-mark-icon" :condensed="true" :can-subresults-open="true"
      :is-any-subresult-open="anyQuestionOpen" @focus-view-click="$emit('focusViewClick')">

      <div v-for="(question, index) in elementData.question" :id="'highlight_key_' + elementKey + question.titleId"
        :key="slotProps.accordionKey + index" class="accordion-item paa-border" :data-titleid="question.titleId"
        :data-urlid="question.urlid" :data-domainid="question.domainId" :data-descid="question.descId">
        <highlight-result :element-data="question" :class="getMarkupClasses(questionRowMarkup(question))" class="accordion-header">
          <div
            class="question-container result-container d-flex align-items-center w-100 justify-content-between position-relative"
            :class="{ collapsed: !this.isOpen(question) }" :title="question.title" @click.stop="toggleOpen(question)">
            <div class="markup-container">
              <span class="question " :class="{ ellipsis: !this.isOpen(question) }">{{ question.title }}</span>
            </div>
            <span class="d-flex align-items-center flex-shrink-0">
              <span class="source-count" data-testid="paa-source-count" :title="sources(question).length + ' sources'">
                <i class="icons link-45deg-icon"></i>{{ sources(question).length }}
              </span>
              <span class="icons chevron-down-icon rotate text-primary" :class="{ up: isOpen(question) }" />
            </span>
          </div>
        </highlight-result>
        <div :id="'key_' + elementKey + question.titleId" class="accordion-collapse collapse"
          :class="{ show: this.isOpen(question) }">
          <div class="accordion-body">
            <div class="d-flex flex-column qa-wrapper pointer">
              <highlight-result :type-id="'desc' + question.descId" :element-data="elementData" class="paa-answer">
                <span v-if="isAiOverview(question.desc, question)" class="ai-overview-container"
                  @click="handleDescriptionClick(index)">
                  <i class="icons ai-overview ai-overview-icon"></i>
                  <span class="ai-overview-text"> {{ computedDesc(question.desc) }}</span>
                </span>
                <span v-else v-html="computedDesc(question.desc)" @click="handleDescriptionClick(index)"></span>
              </highlight-result>
              <div class="paa-sources" data-testid="paa-sources">
                <highlight-result v-for="(source, k) in sources(question)" :key="k" :element-data="source"
                  :class="getMarkupClasses(source)" class="paa-source" :data-urlid="source.urlid">
                  <div class="markup-container">
                    <favicon :size="13" :url="source.url" />
                    <markup-link :resultData="source" :resultType="question.resulttype" />
                  </div>
                </highlight-result>
              </div>
            </div>
          </div>
        </div>
      </div>
    </container-wrapper>
    <ai-overview-dialog :html="dialogHtml" @hideDialog="hideDialog" :show="showDialog" :isPAA="true"
      :question="elementData.question?.[indexOfHtmlToShow]" :date="cardDate"
      :ts="cardMeta.datetime ? Date.parse(cardMeta.datetime) / 1000 : 0" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { unref } from "vue";
import ContainerWrapper from "@/components/card/ContainerWrapper";
import MarkupLink from "@/components/card/MarkupLink.vue";
import Favicon from "@/components/Favicon.vue";
import HighlightResult from "@/components/card/HighlightResult.vue";
import AiOverviewDialog from "@/components/card/AiOverviewDialog.vue";
import { AIO_LABEL_RE, stripDiacritics, isAiOverviewText, paaSources } from "@/utils/aiOverview";
import { detailsPollid, loadResultHtml } from "@/utils/resultHtml";

export default {
  name: "QuestionAnswerBox",
  emits: ["focusViewClick"],
  inject: {
    cardMetaRef: { from: 'cardMeta', default: () => ({}) }
  },
  props: {
    elementData: Object,
    elementKey: String,
  },
  data() {
    return {
      showDialog: false,
      indexOfHtmlToShow: 0, //really, this should be null, but the way the DialogBase component works is that it can't be conditionally rendered, so this needs to be a real value
      fetchedQuestionHtml: [], // DM-10753: question[i].html from /timeline/html, same index as elementData.question
      loadingHtml: false,
    }
  },
  components: {
    ContainerWrapper,
    MarkupLink,
    Favicon,
    HighlightResult,
    AiOverviewDialog,
  },
  computed: {
    ...mapGetters("timeline", {
      isResultOpen: "isResultOpen",
    }),
    ...mapGetters("highlight", { highlightMode: "highlightMode" }),
    ...mapGetters("routeParams", {
      client: "client",
      view: "view",
    }),
    cardMeta() {
      return unref(this.cardMetaRef) || {};
    },
    cardDate() {
      const d = this.cardMeta.datetime ? new Date(this.cardMeta.datetime) : null;
      return d ? d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";
    },
    dialogHtml() {
      const question = this.elementData.question?.[this.indexOfHtmlToShow];
      return question?.html || this.fetchedQuestionHtml[this.indexOfHtmlToShow] || "";
    },
    anyQuestionOpen() {
      return this.elementData.question?.some(question => this.isOpen(question));
    },
  },
  methods: {
    ...mapActions("timeline", ["toggleResultOpen", "initClosedResults"]),
    titleKey(question) {
      return this.elementData.resulttype + question.titleId;
    },
    isOpen(question) {
      return this.isResultOpen(this.titleKey(question));
    },
    toggleOpen(question) {
      this.toggleResultOpen(this.titleKey(question));
    },
    isAiOverview(answer, question) {
      // DM-10753: /timeline marks an answer that has an html view with `hasHtml` instead of sending the html
      return isAiOverviewText(answer) || !!question?.html || !!question?.hasHtml;
    },
    // DM-10271: every source of the answer (a single-source answer is its own source)
    sources(question) {
      return paaSources(question);
    },
    // DM-10271: the question row takes the client's question rating; ownership and authority
    // follow its first source (question.markup is that source's markup).
    questionRowMarkup(question) {
      const rating = question.questionMarkup?.rating ?? question.markup?.rating;
      return { markup: { ...question.markup, rating } };
    },
    hasHtmlView(question) {
      return !!(question?.html || question?.hasHtml);
    },
    computedDesc(desc) {
      const d = desc || "";
      const m = stripDiacritics(d).match(AIO_LABEL_RE);
      if (!m) return d;
      let cut = 0;
      while (cut < d.length && stripDiacritics(d.slice(0, cut)).length < m[0].length) cut++;
      return d.slice(cut);
    },
    handleDescriptionClick(index) {
      const question = this.elementData.question[index];
      if (!question || this.highlightMode || this.showDialog || this.loadingHtml) {
        return;
      }
      // DM-10271: every answer opens the dialog (the question rating and sources live there);
      // only an answer with an html view needs that html first.
      if (!this.hasHtmlView(question) || question.html || this.fetchedQuestionHtml[index]) {
        this.indexOfHtmlToShow = index;
        this.showDialog = true;
        return;
      }
      const pollid = detailsPollid(this.cardMeta, this.view);
      if (!pollid) {
        return;
      }
      this.loadingHtml = true;
      loadResultHtml(this.$api, pollid, this.client?.clientid)
        .then((html) => {
          this.fetchedQuestionHtml = html.question || [];
          if (!this.fetchedQuestionHtml[index]) {
            // DM-10271: still open — the dialog shows the answer text, the rating and the sources
            console.warn("no html for people-also-ask question", index, "of poll", pollid);
          }
          this.indexOfHtmlToShow = index;
          this.showDialog = true;
        })
        .catch((error) => console.error("People-also-ask html failed to load", error))
        .finally(() => { this.loadingHtml = false; });
    },
    hideDialog() {
      this.showDialog = false;
    },

  },
  mounted() {
    const ids = (this.elementData.question ?? []).map((q) => this.titleKey(q));
    this.initClosedResults(ids);
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_icons.scss";
@import "@/scss/_mixins.scss";


@mixin zoomViewSizes($type) {
  .#{$type}-view {
    .rotate {
      font-size: var(--timeline-accordion-toggle-font-size-#{$type});
      margin-right: calc(var(--timeline-accordion-toggle-right-offset-#{$type}) - 1px);
      align-self: baseline;
    }

    .question-container .ellipsis {
      max-width: var(--result-text-width-container-title-#{$type});
    }
  }
}

@include zoomViewSizes("regular");
@include zoomViewSizes("medium");

.card {
  .question-container {
    cursor: default;
    overflow: hidden;

    .rotate {
      line-height: 0px;
      display: inline-block;
      @include hover-clickable-effect;

      &.up {
        transform: rotate(-180deg);
        transition: transform 0.2s ease-in-out;
        @include hover-clickable-effect(rotate(-180deg));
      }
    }

    .markup-container {
      .question {
        color: var(--fb-blue-70);
      }
    }
  }

  .accordion-collapse {
    overflow: hidden;
    text-overflow: ellipsis;

    .accordion-body {
      padding: 0 0.05rem;

      .qa-wrapper {
        // DM-10271: the answer and its sources run to the box's right edge (prototype)
        overflow: auto;

        &>* {
          padding: 0.25rem 0;
        }

        .ai-overview-container {
          display: flex;
          align-items: flex-start;
          cursor: pointer;

          .ai-overview-icon {
            flex-shrink: 0;
            font-size: var(--timeline-font-size-regular);
          }
          .ai-overview-text {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 4;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }

      .markup-container {
        opacity: 0.9;
        color: var(--fb-blue-70);
      }

      // DM-10271: the answer is neutral (it never takes the question's colour); the sources sit
      // indented under it behind a rule, so they read as part of the answer, not as results.
      // sizes and colours from the ticket's prototype
      .paa-answer {
        background-color: #f1f3f5;
        padding: 4px 6px;
        line-height: 18px;
      }

      .paa-sources {
        background-color: #f1f3f5;
        padding: 0 0 3px 18px;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .paa-source {
        min-height: 24px;
        border-left: 2px solid #7B97C7;
        // the grey answer area separates the rows; drop the result separator (HighlightResult)
        // and the link's divider (MarkupLink, for the old single link under the answer)
        box-shadow: none;

        :deep(.markup-link-container) {
          border-top: 0;
          padding-top: 0;
        }

        .markup-container {
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          gap: 5px;
          min-height: 24px;
          padding: 2px 6px;
          color: #000;
          opacity: 1;
        }
      }
    }
  }

  .source-count {
    display: inline-flex;
    align-items: center;
    gap: 1px;
    margin-right: 0.25rem;
    font-size: 0.75em;
    color: var(--fb-blue-70);

    .link-45deg-icon {
      width: 12px;
      height: 12px;
    }
  }
}
</style>
